import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, Trash2, Loader2, Warehouse, Truck, Pencil } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface OpportunityAddress {
  id: string;
  address: string;
  city: string;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  address_type: 'dc' | 'standplaats';
  label: string | null;
  created_at: string;
}

interface OpportunityAddressesSectionProps {
  opportunityId: string;
  organizationName?: string;
}

export function OpportunityAddressesSection({ opportunityId, organizationName }: OpportunityAddressesSectionProps) {
  const { toast } = useToast();
  const [addresses, setAddresses] = useState<OpportunityAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalAddress, setOriginalAddress] = useState<string>('');
  
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    postal_code: '',
    address_type: 'dc' as 'dc' | 'standplaats',
    label: '',
  });

  useEffect(() => {
    fetchAddresses();
  }, [opportunityId]);

  const fetchAddresses = async () => {
    try {
      const { data, error } = await supabase
        .from('opportunity_addresses')
        .select('*')
        .eq('opportunity_id', opportunityId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setAddresses((data || []) as OpportunityAddress[]);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const geocodeAddress = async (address: string, city: string, postalCode: string): Promise<{ lat: number; lng: number } | null> => {
    try {
      const query = [address, postalCode, city, 'Netherlands'].filter(Boolean).join(', ');
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
        { headers: { 'User-Agent': 'Greenvision Charging Portal' } }
      );
      
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const resetForm = () => {
    setFormData({ address: '', city: '', postal_code: '', address_type: 'dc', label: '' });
    setEditingId(null);
    setOriginalAddress('');
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (addr: OpportunityAddress) => {
    setFormData({
      address: addr.address,
      city: addr.city,
      postal_code: addr.postal_code || '',
      address_type: addr.address_type,
      label: addr.label || '',
    });
    setEditingId(addr.id);
    setOriginalAddress(addr.address);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.address.trim() || !formData.city.trim()) {
      toast({ title: 'Adres en stad zijn verplicht', variant: 'destructive' });
      return;
    }

    setSaving(true);
    setGeocoding(true);

    try {
      // Only re-geocode if address changed or it's a new entry
      let coords: { lat: number; lng: number } | null = null;
      const addressChanged = !editingId || formData.address !== originalAddress;
      
      if (addressChanged) {
        coords = await geocodeAddress(formData.address, formData.city, formData.postal_code);
      }
      setGeocoding(false);

      if (editingId) {
        // Update existing
        const updateData: any = {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code || null,
          address_type: formData.address_type,
          label: formData.label || null,
        };
        
        // Only update coords if we re-geocoded
        if (addressChanged && coords) {
          updateData.latitude = coords.lat;
          updateData.longitude = coords.lng;
        }

        const { error } = await supabase
          .from('opportunity_addresses')
          .update(updateData)
          .eq('id', editingId);

        if (error) throw error;
        toast({ title: 'Adres bijgewerkt' });
      } else {
        // Insert new
        const { error } = await supabase
          .from('opportunity_addresses')
          .insert({
            opportunity_id: opportunityId,
            address: formData.address,
            city: formData.city,
            postal_code: formData.postal_code || null,
            address_type: formData.address_type,
            label: formData.label || null,
            latitude: coords?.lat || null,
            longitude: coords?.lng || null,
          });

        if (error) throw error;
        toast({ title: 'Adres toegevoegd' });
      }

      resetForm();
      setModalOpen(false);
      fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      toast({ title: 'Fout bij opslaan', variant: 'destructive' });
    } finally {
      setSaving(false);
      setGeocoding(false);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    try {
      const { error } = await supabase
        .from('opportunity_addresses')
        .delete()
        .eq('id', addressId);

      if (error) throw error;
      
      toast({ title: 'Adres verwijderd' });
      fetchAddresses();
    } catch (error) {
      console.error('Error deleting address:', error);
      toast({ title: 'Fout bij verwijderen', variant: 'destructive' });
    }
  };

  const getTypeIcon = (type: 'dc' | 'standplaats') => {
    return type === 'dc' 
      ? <Warehouse className="h-4 w-4" />
      : <Truck className="h-4 w-4" />;
  };

  const getTypeBadge = (type: 'dc' | 'standplaats') => {
    return type === 'dc' 
      ? <Badge className="bg-purple-500 hover:bg-purple-600">DC</Badge>
      : <Badge className="bg-blue-500 hover:bg-blue-600">Standplaats</Badge>;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Locaties {organizationName || 'Vervoerder'}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={openAddModal}>
            <Plus className="h-4 w-4 mr-1" />
            Toevoegen
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {addresses.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nog geen DC's of standplaatsen toegevoegd
            </p>
          ) : (
            addresses.map((addr) => (
              <div key={addr.id} className="flex items-start gap-3 p-2 rounded-lg bg-muted/50">
                <div className="mt-0.5">
                  {getTypeIcon(addr.address_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {getTypeBadge(addr.address_type)}
                    {addr.label && (
                      <span className="text-sm font-medium">{addr.label}</span>
                    )}
                  </div>
                  <p className="text-sm mt-1">{addr.address}</p>
                  <p className="text-xs text-muted-foreground">
                    {[addr.postal_code, addr.city].filter(Boolean).join(' ')}
                  </p>
                  {!addr.latitude && (
                    <p className="text-xs text-orange-500 mt-1">⚠️ Niet geocoded</p>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  onClick={() => openEditModal(addr)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => handleDeleteAddress(addr.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) { resetForm(); setModalOpen(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Locatie bewerken' : 'Locatie toevoegen'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Type *</Label>
              <Select
                value={formData.address_type}
                onValueChange={(value: 'dc' | 'standplaats') => 
                  setFormData(prev => ({ ...prev, address_type: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dc">
                    <div className="flex items-center gap-2">
                      <Warehouse className="h-4 w-4" />
                      <span>DC (eigen)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="standplaats">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      <span>Standplaats</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Label (optioneel)</Label>
              <Input
                placeholder="bijv. DC Noord, Hoofdvestiging"
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
              />
            </div>

            <div>
              <Label>Adres *</Label>
              <Input
                placeholder="Straatnaam en huisnummer"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Postcode</Label>
                <Input
                  placeholder="1234 AB"
                  value={formData.postal_code}
                  onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                />
              </div>
              <div>
                <Label>Stad *</Label>
                <Input
                  placeholder="Stad"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setModalOpen(false); }}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || !formData.address.trim() || !formData.city.trim()}
            >
              {geocoding ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Geocoding...
                </>
              ) : saving ? (
                'Opslaan...'
              ) : editingId ? (
                'Bijwerken'
              ) : (
                'Toevoegen'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
